import { Controller } from "@hotwired/stimulus";
import { isTurboStream, withdrawalFailed } from "./pushToCardSendingHelpers.mjs";

// Card withdrawal after the PIN. Finalize gets the quote and captures it in one
// request, so the sending screen replaces the keypad from the moment the PIN is
// submitted until the result arrives.
//
// The withdrawal is submitted once. A failure goes back to Confirm Withdrawal
// with its message and key. The server replaces a key whose withdrawal failed,
// and keeps one that may still be running so a resubmit is refused.
export default class extends Controller {
  static targets = ["keepOpen", "stillWorking", "returnForm", "fallbackError"];
  static outlets = ["pin"];
  static values = { slowAfter: { type: Number, default: 8000 } };

  // idle -> sending -> failed
  state = "idle";

  disconnect() {
    clearTimeout(this.slowTimer);
  }

  pinOutletConnected(pin) {
    this.pinForm = pin.formTarget;
    this.pinForm.addEventListener("submit", this.onSubmit);
    this.pinForm.addEventListener("turbo:before-fetch-response", this.onWithdrawalResponse);
    this.pinForm.addEventListener("turbo:submit-end", this.onWithdrawalEnd);
  }

  pinOutletDisconnected() {
    this.pinForm?.removeEventListener("submit", this.onSubmit);
    this.pinForm?.removeEventListener("turbo:before-fetch-response", this.onWithdrawalResponse);
    this.pinForm?.removeEventListener("turbo:submit-end", this.onWithdrawalEnd);
  }

  // Keys pressed on the hidden keypad can submit again; only the first goes through.
  onSubmit = (event) => {
    if (this.state !== "idle") {
      event.preventDefault();
      return;
    }
    this.state = "sending";
    this.showScreen();
  };

  showScreen() {
    this.pinOutlet.element.classList.add("hidden");
    this.element.classList.replace("hidden", "flex");
    this.slowTimer = setTimeout(() => {
      this.keepOpenTarget.classList.add("hidden");
      this.stillWorkingTarget.classList.remove("hidden");
    }, this.slowAfterValue);
  }

  hideScreen() {
    clearTimeout(this.slowTimer);
    this.element.classList.replace("flex", "hidden");
    this.keepOpenTarget.classList.remove("hidden");
    this.stillWorkingTarget.classList.add("hidden");
    this.pinOutlet.element.classList.remove("hidden");
  }

  // The result screen renders as usual. A failure is taken here, before
  // Turbo's stream observer on the document would draw it over this screen.
  onWithdrawalResponse = async (event) => {
    if (this.state !== "sending") return;
    const { fetchResponse } = event.detail;
    if (!withdrawalFailed(fetchResponse)) return;

    event.preventDefault();
    event.stopPropagation();
    this.state = "failed";
    const streamHtml = isTurboStream(fetchResponse.contentType) ? await fetchResponse.responseHTML : null;
    this.returnToConfirm(streamHtml || this.fallbackErrorTarget.innerHTML);
  };

  // A dropped connection has no response to inspect.
  onWithdrawalEnd = (event) => {
    if (this.state === "sending" && event.detail.error) {
      this.state = "failed";
      this.returnToConfirm(this.fallbackErrorTarget.innerHTML);
    }
  };

  returnToConfirm(streamHtml) {
    this.pendingError = streamHtml;
    this.confirmWillRender = false;
    this.returnFormTarget.requestSubmit();
  }

  // Confirm Withdrawal replaces this screen, so the message is drawn once it has.
  onConfirmResponse(event) {
    const { fetchResponse } = event.detail;
    this.confirmWillRender = fetchResponse.succeeded && !isTurboStream(fetchResponse.contentType);
    if (!this.confirmWillRender) return;

    const streamHtml = this.pendingError;
    this.element.closest("turbo-frame")?.addEventListener(
      "turbo:frame-render",
      () => Turbo.renderStreamMessage(streamHtml),
      { once: true },
    );
  }

  // Confirm Withdrawal could not be shown. Back to the keypad with the message.
  // Its payload keeps the old key; once that withdrawal has run, finalize
  // refuses it and the next failure returns here for a new one.
  onConfirmEnd() {
    if (this.confirmWillRender) return;
    this.hideScreen();
    this.state = "idle";
    Turbo.renderStreamMessage(this.pendingError);
    this.pinOutlet.clearPin();
  }
}
